/**
 * Per-step structured editor for `McpStep[]`.
 *
 * Replaces the legacy flat `<Textarea>` editors for `steps` (one line
 * per step) + `expectedResult` (single final assertion). After #596
 * the canonical step shape is `McpStep[]` — each step carries its own
 * `perform` (action prose) and `expect[]` (per-step assertions).
 *
 * #718 slice 1: `expect` is now an array of typed assertion clauses
 * (`TypedAssertion`), not free text. Each clause has a `kind` selector
 * and a kind-specific value control: a boolean toggle for
 * visible/checked/enabled, a text input for the *_equals / contains /
 * url kinds, a number input for count_equals, and a free-text input
 * for the `freeform` escape hatch. Switching kind resets to that
 * kind's default-shaped clause.
 *
 * Buffered state semantics live in the parent (TestDetail.tsx): this
 * component is a controlled input, dispatching every change up via
 * `onChange`. The parent owns dirty detection and save batching.
 */
import { Plus, Trash2, X } from 'lucide-react'
import { Button, Input, Label, Textarea } from '@/components/ui'
import type { McpStep, TypedAssertion } from '@/types'

export interface McpStepsEditorProps {
  value: McpStep[]
  onChange: (next: McpStep[]) => void
  disabled?: boolean
}

/** All assertion kinds, in the order they appear in the kind dropdown. */
const ASSERTION_KINDS: TypedAssertion['kind'][] = [
  'visible',
  'checked',
  'enabled',
  'text_equals',
  'text_contains',
  'value_equals',
  'count_equals',
  'url_matches',
  'freeform',
]

/** Human-readable label for each kind, shown in the dropdown. */
const KIND_LABELS: Record<TypedAssertion['kind'], string> = {
  visible: 'is visible',
  checked: 'is checked',
  enabled: 'is enabled',
  text_equals: 'text equals',
  text_contains: 'text contains',
  value_equals: 'value equals',
  count_equals: 'count equals',
  url_matches: 'URL matches',
  freeform: 'freeform',
}

/** A fresh clause of the given kind, with empty/default field values. */
function defaultAssertion(kind: TypedAssertion['kind']): TypedAssertion {
  switch (kind) {
    case 'visible':
    case 'checked':
    case 'enabled':
      return { kind, target: '', value: true }
    case 'text_equals':
    case 'text_contains':
    case 'value_equals':
      return { kind, target: '', value: '' }
    case 'count_equals':
      return { kind, target: '', value: 0 }
    case 'url_matches':
      return { kind, pattern: '' }
    case 'freeform':
      return { kind, text: '' }
  }
}

/** Carry over `target` when switching between target-bearing kinds so
 * an operator changing "is visible" → "text contains" doesn't lose the
 * description they already typed. */
function changeKind(prev: TypedAssertion, kind: TypedAssertion['kind']): TypedAssertion {
  const next = defaultAssertion(kind)
  if ('target' in next && 'target' in prev) {
    next.target = prev.target
  }
  return next
}

const BOOL_KINDS = new Set<TypedAssertion['kind']>(['visible', 'checked', 'enabled'])
const TEXT_VALUE_KINDS = new Set<TypedAssertion['kind']>([
  'text_equals',
  'text_contains',
  'value_equals',
])

export function McpStepsEditor({ value, onChange, disabled }: McpStepsEditorProps) {
  const steps = value ?? []

  function updateStep(i: number, patch: Partial<McpStep>) {
    onChange(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)))
  }
  function updateExpect(stepIdx: number, expectIdx: number, next: TypedAssertion) {
    onChange(
      steps.map((s, idx) =>
        idx === stepIdx
          ? { ...s, expect: s.expect.map((e, ei) => (ei === expectIdx ? next : e)) }
          : s,
      ),
    )
  }
  function addExpect(stepIdx: number) {
    onChange(
      steps.map((s, idx) =>
        idx === stepIdx ? { ...s, expect: [...s.expect, defaultAssertion('visible')] } : s,
      ),
    )
  }
  function removeExpect(stepIdx: number, expectIdx: number) {
    onChange(
      steps.map((s, idx) =>
        idx === stepIdx ? { ...s, expect: s.expect.filter((_, ei) => ei !== expectIdx) } : s,
      ),
    )
  }
  function addStep() {
    onChange([...steps, { perform: '', expect: [] }])
  }
  function removeStep(i: number) {
    onChange(steps.filter((_, idx) => idx !== i))
  }

  if (steps.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground italic">No steps yet.</p>
        <Button variant="outline" size="sm" onClick={addStep} disabled={disabled}>
          <Plus className="h-3 w-3 mr-1" /> Add step
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {steps.map((step, i) => (
        <div key={i} className="border rounded-md p-3 space-y-2 bg-muted/10">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Step {i + 1}</span>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="icon-sm"
              title="Remove step"
              onClick={() => removeStep(i)}
              disabled={disabled}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Action</Label>
            <Textarea
              value={step.perform ?? ''}
              onChange={(e) => updateStep(i, { perform: e.target.value })}
              rows={2}
              className="text-sm"
              placeholder="What this step does (e.g. 'Click the Search button')"
              disabled={disabled}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Expected outcomes</Label>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => addExpect(i)}
                disabled={disabled}
              >
                <Plus className="h-3 w-3 mr-0.5" /> Add expect
              </Button>
            </div>
            {step.expect.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No assertions for this step (the step still runs; absence of expects means we don't verify state at this point).
              </p>
            ) : (
              <div className="space-y-1.5">
                {step.expect.map((assertion, ei) => (
                  <AssertionRow
                    key={ei}
                    assertion={assertion}
                    disabled={disabled}
                    onChange={(next) => updateExpect(i, ei, next)}
                    onRemove={() => removeExpect(i, ei)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addStep} disabled={disabled}>
        <Plus className="h-3 w-3 mr-1" /> Add step
      </Button>
    </div>
  )
}

interface AssertionRowProps {
  assertion: TypedAssertion
  disabled?: boolean
  onChange: (next: TypedAssertion) => void
  onRemove: () => void
}

/** One typed-assertion clause: a kind dropdown, the kind-specific
 * fields (target + value / pattern / text), and a remove button. */
function AssertionRow({ assertion, disabled, onChange, onRemove }: AssertionRowProps) {
  return (
    <div className="flex flex-wrap items-center gap-1 rounded border bg-background p-1.5">
      <select
        value={assertion.kind}
        onChange={(e) => onChange(changeKind(assertion, e.target.value as TypedAssertion['kind']))}
        disabled={disabled}
        className="h-7 rounded border bg-background px-1 text-xs"
        aria-label="Assertion kind"
      >
        {ASSERTION_KINDS.map((k) => (
          <option key={k} value={k}>
            {KIND_LABELS[k]}
          </option>
        ))}
      </select>

      {'target' in assertion && (
        <Input
          value={assertion.target}
          onChange={(e) => onChange({ ...assertion, target: e.target.value })}
          placeholder="Target (e.g. 'the search-results heading')"
          className="h-7 text-sm flex-1 min-w-[12rem]"
          disabled={disabled}
        />
      )}

      {BOOL_KINDS.has(assertion.kind) && 'value' in assertion && (
        <select
          value={String(assertion.value)}
          onChange={(e) =>
            onChange({ ...assertion, value: e.target.value === 'true' } as TypedAssertion)
          }
          disabled={disabled}
          className="h-7 rounded border bg-background px-1 text-xs"
          aria-label="Expected value"
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      )}

      {TEXT_VALUE_KINDS.has(assertion.kind) && 'value' in assertion && (
        <Input
          value={String(assertion.value)}
          onChange={(e) => onChange({ ...assertion, value: e.target.value } as TypedAssertion)}
          placeholder="Expected text"
          className="h-7 text-sm flex-1 min-w-[8rem]"
          disabled={disabled}
        />
      )}

      {assertion.kind === 'count_equals' && (
        <Input
          type="number"
          min={0}
          value={assertion.value}
          onChange={(e) =>
            onChange({ ...assertion, value: Math.max(0, Number(e.target.value) || 0) })
          }
          className="h-7 text-sm w-20"
          disabled={disabled}
        />
      )}

      {assertion.kind === 'url_matches' && (
        <Input
          value={assertion.pattern}
          onChange={(e) => onChange({ ...assertion, pattern: e.target.value })}
          placeholder="URL substring or pattern"
          className="h-7 text-sm flex-1 min-w-[10rem]"
          disabled={disabled}
        />
      )}

      {assertion.kind === 'freeform' && (
        <Input
          value={assertion.text}
          onChange={(e) => onChange({ ...assertion, text: e.target.value })}
          placeholder="Free-text assertion"
          className="h-7 text-sm flex-1 min-w-[12rem]"
          disabled={disabled}
        />
      )}

      <Button
        variant="ghost"
        size="icon-sm"
        title="Remove expect"
        onClick={onRemove}
        disabled={disabled}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  )
}

/** Canonical key order for serializing a `TypedAssertion`. Passed as the
 * `JSON.stringify` replacer so the output is independent of property
 * insertion order — two structurally-equal clauses then compare equal.
 * Mirrors `ASSERTION_CANONICAL_KEYS` in the server's `mcpStep.ts` (the
 * frontend cannot import server modules). */
const ASSERTION_KEY_ORDER = ['kind', 'pattern', 'target', 'text', 'value']

/** Deep equality check for two McpStep[] arrays. Used by the parent
 * (TestDetail) for dirty detection — typed assertions are compared
 * structurally via canonical (key-order-independent) JSON; a direct
 * walk of the rest is cheap and unambiguous. */
export function mcpStepsEqual(a: McpStep[], b: McpStep[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]
    const y = b[i]
    if ((x.perform ?? '') !== (y.perform ?? '')) return false
    const xe = x.expect ?? []
    const ye = y.expect ?? []
    if (xe.length !== ye.length) return false
    for (let j = 0; j < xe.length; j++) {
      if (JSON.stringify(xe[j], ASSERTION_KEY_ORDER) !== JSON.stringify(ye[j], ASSERTION_KEY_ORDER)) return false
    }
  }
  return true
}
